import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  Eye, EyeOff, LogIn, UserPlus, Heart, Sparkles, GraduationCap, 
  Briefcase, HandHeart, HelpCircle, ArrowRight, BookOpen, MapPin,
  Download, MessageSquare, Calendar, Shield, ChevronDown, Users
} from 'lucide-react';

const COMMUNITY_IMG_1 = "https://customer-assets.emergentagent.com/job_0e3427ab-d5b3-4fa8-993a-18bab96ac733/artifacts/64sfcjuv_IMG_6507-compressed.jpeg";
const COMMUNITY_IMG_2 = "https://customer-assets.emergentagent.com/job_0e3427ab-d5b3-4fa8-993a-18bab96ac733/artifacts/8hosln5z_IMG_6516-compressed.jpeg";

const tagOptions = [
  { key: 'autistic', label: 'I am autistic', icon: Sparkles, color: 'border-[hsl(195,77%,45%)] bg-[hsl(195,77%,95%)]' },
  { key: 'parent', label: 'I am a parent of an autistic person', icon: Heart, color: 'border-[hsl(166,56%,45%)] bg-[hsl(166,56%,95%)]' },
  { key: 'teacher', label: 'I am a teacher or educator', icon: GraduationCap, color: 'border-[hsl(54,100%,45%)] bg-[hsl(54,100%,95%)]' },
  { key: 'employer', label: 'I am an employer', icon: Briefcase, color: 'border-[hsl(27,100%,55%)] bg-[hsl(27,100%,95%)]' },
  { key: 'volunteer', label: 'I want to volunteer', icon: HandHeart, color: 'border-[hsl(346,100%,65%)] bg-[hsl(346,100%,95%)]' },
  { key: 'suspect_autistic', label: 'I suspect I might be autistic', icon: HelpCircle, color: 'border-[hsl(320,68%,51%)] bg-[hsl(320,68%,95%)]' }
];

const hubFeatures = [
  { icon: BookOpen, title: 'Education & Information', description: 'Learn about autism from a neurodiversity perspective.' },
  { icon: MapPin, title: 'Service Directory', description: 'Find therapists, specialists, and support services.' },
  { icon: Download, title: 'Tools & Downloads', description: 'Visual schedules, templates, and resources.' },
  { icon: MessageSquare, title: 'Community Forum', description: 'Connect with other families and find peer support.' },
  { icon: Calendar, title: 'Events & Webinars', description: 'Join monthly webinars and family events.' },
  { icon: Shield, title: 'Training & Advocacy', description: 'Autism acceptance training for organisations.' }
];

const FUNDRAISING_GOAL = 60000;
const FUNDRAISING_RAISED = 0;

export default function LandingPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Login form state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    tags: {
      autistic: false,
      parent: false,
      teacher: false,
      employer: false,
      volunteer: false,
      suspect_autistic: false
    },
    newsletter: false,
    accept_terms: false
  });

  const handleTagChange = (tag) => {
    setFormData(prev => ({
      ...prev,
      tags: {
        ...prev.tags,
        [tag]: !prev.tags[tag]
      }
    }));
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const user = await login(loginEmail, loginPassword);
      toast.success(`Welcome back, ${user.first_name}!`);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!formData.accept_terms) {
      setError('You must accept the terms and conditions');
      return;
    }

    const hasTag = Object.values(formData.tags).some(v => v);
    if (!hasTag) {
      setError('Please select at least one option that describes you');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...registerData } = formData;
      const user = await register(registerData);
      toast.success(`Welcome to Autism Acceptance, ${user.first_name}!`);
      
      if (formData.tags.volunteer) {
        navigate('/volunteer');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const scrollToFeatures = () => {
    document.getElementById('features-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const progressPercent = (FUNDRAISING_RAISED / FUNDRAISING_GOAL) * 100;

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[hsl(346,85%,95%)] via-background to-[hsl(27,100%,95%)] py-16 lg:py-24">
        <div className="container-main">
          {/* Main Title - Large and Prominent */}
          <div className="text-center mb-12">
            <Badge className="bg-[hsl(346,85%,90%)] text-[hsl(346,85%,40%)] border-[hsl(346,85%,70%)] mb-6 text-sm px-4 py-1">
              Cayman Islands
            </Badge>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black tracking-tight text-foreground mb-6 leading-tight">
              Autism Acceptance<br />
              <span className="text-gradient-brand">Resources Hub</span>
            </h1>
            <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
              Your central place for autism information, support services, community connection, and resources in the Cayman Islands.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Left - Info & Images */}
            <div className="space-y-6">
              <p className="text-lg text-muted-foreground">
                Register or sign in to access all resources, connect with the community, and stay informed.
              </p>

              {/* Community Images */}
              <div className="grid grid-cols-2 gap-4">
                <img 
                  src={COMMUNITY_IMG_1} 
                  alt="Autism Acceptance community event" 
                  className="rounded-2xl shadow-lg w-full h-48 object-cover"
                />
                <img 
                  src={COMMUNITY_IMG_2} 
                  alt="Autism Acceptance volunteers" 
                  className="rounded-2xl shadow-lg w-full h-48 object-cover"
                />
              </div>
              
              {/* Quick Stats - Updated with dark mode support */}
              <div className="grid grid-cols-3 gap-4 pt-4">
                <div className="text-center p-4 bg-white/80 dark:bg-gray-800/80 rounded-xl backdrop-blur-sm">
                  <div className="text-3xl font-bold text-primary">50+</div>
                  <div className="text-sm text-foreground">Service Providers</div>
                </div>
                <div className="text-center p-4 bg-white/80 dark:bg-gray-800/80 rounded-xl backdrop-blur-sm">
                  <div className="text-3xl font-bold text-primary">30+</div>
                  <div className="text-sm text-foreground">Resources</div>
                </div>
                <div className="text-center p-4 bg-white/80 dark:bg-gray-800/80 rounded-xl backdrop-blur-sm">
                  <div className="text-3xl font-bold text-primary">Free</div>
                  <div className="text-sm text-foreground">To Join</div>
                </div>
              </div>
            </div>

            {/* Right - Auth Forms */}
            <div>
              <Card className="card-base shadow-xl">
                <CardHeader className="text-center pb-4">
                  <div className="flex justify-center gap-2 mb-4">
                    <Button 
                      variant={mode === 'login' ? 'default' : 'outline'}
                      onClick={() => { setMode('login'); setError(''); }}
                      className={mode === 'login' ? 'btn-primary' : ''}
                      data-testid="switch-to-login"
                    >
                      Sign In
                    </Button>
                    <Button 
                      variant={mode === 'register' ? 'default' : 'outline'}
                      onClick={() => { setMode('register'); setError(''); }}
                      className={mode === 'register' ? 'btn-primary' : ''}
                      data-testid="switch-to-register"
                    >
                      Join Hub
                    </Button>
                  </div>
                  <CardTitle className="text-xl">
                    {mode === 'login' ? 'Welcome Back' : 'Join the Hub'}
                  </CardTitle>
                  <CardDescription>
                    {mode === 'login' 
                      ? 'Sign in to access your account and resources' 
                      : 'Create your account to access all features'}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {error && (
                    <Alert variant="destructive" className="mb-4" data-testid="auth-error">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {mode === 'login' ? (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="login-email">Email</Label>
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="your@email.com"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          required
                          data-testid="login-email"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="login-password">Password</Label>
                        <div className="relative">
                          <Input
                            id="login-password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Your password"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            required
                            data-testid="login-password"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8"
                            onClick={() => setShowPassword(!showPassword)}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </Button>
                        </div>
                      </div>

                      <div className="text-right">
                        <Link 
                          to="/forgot-password" 
                          className="text-sm text-primary hover:underline"
                          data-testid="forgot-password-link"
                        >
                          Forgot password?
                        </Link>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full btn-primary" 
                        disabled={loading}
                        data-testid="login-submit"
                      >
                        {loading ? 'Signing in...' : (
                          <>
                            <LogIn className="mr-2 h-4 w-4" />
                            Sign In
                          </>
                        )}
                      </Button>
                    </form>
                  ) : (
                    <form onSubmit={handleRegister} className="space-y-4">
                      {/* Pathway Selection */}
                      <div className="space-y-2 p-3 bg-muted/30 rounded-xl">
                        <Label className="text-sm font-semibold">I identify as... (select all that apply) *</Label>
                        <div className="grid grid-cols-1 gap-2">
                          {tagOptions.map((option) => (
                            <label
                              key={option.key}
                              className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-all text-sm ${
                                formData.tags[option.key] 
                                  ? option.color
                                  : 'border-border hover:border-primary/50 bg-background'
                              }`}
                              data-testid={`tag-${option.key}`}
                            >
                              <Checkbox
                                checked={formData.tags[option.key]}
                                onCheckedChange={() => handleTagChange(option.key)}
                              />
                              <option.icon className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Personal Info */}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="first_name" className="text-sm">First Name *</Label>
                          <Input
                            id="first_name"
                            value={formData.first_name}
                            onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                            required
                            data-testid="register-first-name"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="last_name" className="text-sm">Last Name *</Label>
                          <Input
                            id="last_name"
                            value={formData.last_name}
                            onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                            required
                            data-testid="register-last-name"
                          />
                        </div>
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="email" className="text-sm">Email *</Label>
                        <Input
                          id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          required
                          data-testid="register-email"
                        />
                      </div>

                      <div className="space-y-1">
                        <Label htmlFor="phone" className="text-sm">Phone Number *</Label>
                        <Input
                          id="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          required
                          data-testid="register-phone"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="password" className="text-sm">Password *</Label>
                          <div className="relative">
                            <Input
                              id="password"
                              type={showPassword ? 'text' : 'password'}
                              value={formData.password}
                              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                              required
                              minLength={8}
                              data-testid="register-password"
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                            </Button>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="confirmPassword" className="text-sm">Confirm *</Label>
                          <Input
                            id="confirmPassword"
                            type={showPassword ? 'text' : 'password'}
                            value={formData.confirmPassword}
                            onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                            required
                            data-testid="register-confirm-password"
                          />
                        </div>
                      </div>

                      {/* Newsletter */}
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="newsletter"
                          checked={formData.newsletter}
                          onCheckedChange={(checked) => setFormData({ ...formData, newsletter: checked })}
                          data-testid="register-newsletter"
                        />
                        <Label htmlFor="newsletter" className="cursor-pointer text-sm">
                          Subscribe to monthly newsletter
                        </Label>
                      </div>

                      {/* Terms */}
                      <div className="p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Checkbox
                            id="terms"
                            checked={formData.accept_terms}
                            onCheckedChange={(checked) => setFormData({ ...formData, accept_terms: checked })}
                            className="mt-0.5"
                            data-testid="register-terms"
                          />
                          <Label htmlFor="terms" className="cursor-pointer text-xs text-muted-foreground">
                            I accept the Terms & Conditions and understand that my data will be stored securely, 
                            information is for educational purposes only, and Autism Acceptance is not liable for 
                            actions taken based on provided information.
                          </Label>
                        </div>
                      </div>

                      <Button 
                        type="submit" 
                        className="w-full btn-primary" 
                        disabled={loading}
                        data-testid="register-submit"
                      >
                        {loading ? 'Creating account...' : (
                          <>
                            <UserPlus className="mr-2 h-4 w-4" />
                            Create Account
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
          
          {/* Scroll Arrow */}
          <div className="flex justify-center mt-12">
            <button 
              onClick={scrollToFeatures}
              className="animate-bounce p-2 rounded-full bg-primary/10 hover:bg-primary/20 transition-colors"
              aria-label="Scroll down to see more"
            >
              <ChevronDown className="h-8 w-8 text-primary" />
            </button>
          </div>
        </div>
      </section>

      {/* Fundraising Banner */}
      <section className="bg-gradient-to-r from-[hsl(346,85%,55%)] to-[hsl(27,100%,55%)] text-white py-8" data-testid="fundraising-banner">
        <div className="container-main">
          <div className="max-w-4xl mx-auto text-center space-y-4">
            <Badge className="bg-white/20 text-white border-none">Major Fundraising Goal</Badge>
            <h2 className="text-xl md:text-2xl font-bold">
              Help Us Build Cayman's First Neurodivergent Community Centre
            </h2>
            <p className="text-white/90 text-sm">
              We are raising $60,000 to establish a safe space for autistic individuals with a sensory room, 
              educational workshops, and respite opportunities.
            </p>
            
            {/* Progress Bar */}
            <div className="max-w-md mx-auto space-y-2">
              <div className="flex justify-between text-sm">
                <span>Raised: ${FUNDRAISING_RAISED.toLocaleString()}</span>
                <span>Goal: ${FUNDRAISING_GOAL.toLocaleString()}</span>
              </div>
              <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${Math.max(progressPercent, 2)}%` }}
                />
              </div>
            </div>

            <a href="https://donate.autismacceptance.ky/" target="_blank" rel="noopener noreferrer">
              <Button className="bg-white text-[hsl(346,85%,45%)] hover:bg-white/90" size="lg" data-testid="donate-hero-btn">
                <Heart className="mr-2 h-5 w-5" />
                Donate Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What's Inside the Hub */}
      <section id="features-section" className="section-spacing bg-muted/30" data-testid="features-section">
        <div className="container-main">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-[hsl(27,100%,90%)] text-[hsl(27,100%,35%)]">Member Benefits</Badge>
            <h2 className="text-2xl md:text-3xl font-bold mb-3">What's Inside the Hub</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Join our community to access all these resources and connect with families across the Cayman Islands.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {hubFeatures.map((feature) => (
              <Card key={feature.title} className="card-base h-full">
                <CardContent className="pt-6">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-3">
                    <feature.icon className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-1">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* CTA after features */}
          <div className="text-center mt-10">
            <Button 
              size="lg" 
              className="btn-primary"
              onClick={() => { setMode('register'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
              data-testid="join-hub-cta-1"
            >
              <Users className="mr-2 h-5 w-5" />
              Join the Hub Today
            </Button>
          </div>
        </div>
      </section>

      {/* About Section - Created by Autism Parents */}
      <section className="section-spacing">
        <div className="container-main">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <Badge className="bg-[hsl(27,100%,90%)] text-[hsl(27,100%,35%)]">About This Hub</Badge>
            <h2 className="text-2xl md:text-3xl font-bold">
              Created by Autism Parents, For Our Community
            </h2>
            <p className="text-muted-foreground">
              The Autism Acceptance Resources Hub was created to fill a gap in the Cayman Islands. 
              A central place where autistic individuals, families, educators, and employers can find 
              reliable, neurodiversity-affirming information and support.
            </p>
            <p className="text-muted-foreground">
              Our board consists entirely of autism parents who understand firsthand the challenges 
              of navigating diagnosis, finding services, and advocating for inclusion.
            </p>
            
            {/* CTA after Created by section */}
            <div className="pt-4">
              <Button 
                size="lg" 
                className="btn-primary"
                onClick={() => { setMode('register'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                data-testid="join-hub-cta-2"
              >
                <UserPlus className="mr-2 h-5 w-5" />
                Become a Member
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground pt-6">
              Registered Non-Profit Organisation of the Cayman Islands, NP-693
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
